import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, MapPin, TrendingUp, ShieldCheck, Info, Copy, Share2, Camera, FileText, Link as LinkIcon, ChevronRight, Edit2, Check, Instagram, Facebook, Youtube, Twitter, Globe, Mail, Phone, MessageSquare, Linkedin, Star, Home, Video, X, Plus, Building2, ExternalLink } from 'lucide-react';
// --- Types ---
type Step = 1 | 1.5 | 2 | 3 | 4 | 5; // 1.5 is Screen 1b (OTP)
type ConfirmStep = 'identity' | 'work' | 'social' | 'reviews' | 'contact';
const CITIES = ['San Francisco, CA', 'Los Angeles, CA', 'San Diego, CA', 'New York, NY', 'Austin, TX', 'Dallas, TX', 'Miami, FL', 'Seattle, WA', 'Chicago, IL', 'Denver, CO'];
// --- Main Component ---
export function Onboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Routing State
  const [step, setStep] = useState<Step>(1);
  const [confirmStep, setConfirmStep] = useState<ConfirmStep>('identity');
  // Form State
  const [slug, setSlug] = useState(searchParams.get('slug') || '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('CA');
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [showResentMsg, setShowResentMsg] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // UI State
  const [isWrongPerson, setIsWrongPerson] = useState(false);
  const [reviewsState, setReviewsState] = useState({
    google: true,
    zillow: true,
    yelp: true,
    facebook: true
  });
  const [showContactInfo, setShowContactInfo] = useState(true);
  // Work Record State
  const [hiddenCards, setHiddenCards] = useState({
    transactions: false,
    performance: false
  });
  // Online Presence State
  const [socialAccounts, setSocialAccounts] = useState([{
    id: '1',
    platform: 'Instagram',
    icon: Instagram,
    handle: '@marcuschen.re',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }, {
    id: '2',
    platform: 'Facebook',
    icon: Facebook,
    handle: 'facebook.com/marcuschenrealty',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }, {
    id: '3',
    platform: 'YouTube',
    icon: Youtube,
    handle: 'youtube.com/@thechengroup',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }]);
  const [websiteAccounts, setWebsiteAccounts] = useState([{
    id: '1',
    platform: 'Website',
    icon: Globe,
    handle: 'marcuschen.com',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }, {
    id: '2',
    platform: 'Compass',
    icon: Building2,
    handle: 'compass.com/marcuschen',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }, {
    id: '3',
    platform: 'Realtor.com',
    icon: ExternalLink,
    handle: 'realtor.com/marcuschen',
    status: 'unconfirmed' as const,
    isEditing: false,
    visible: true
  }]);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const [newWebsiteTitle, setNewWebsiteTitle] = useState('');
  const [newWebsiteUrl, setNewWebsiteUrl] = useState('');
  // Auto-generate slug if not provided
  const displaySlug = slug || (firstName || lastName ? `${firstName}${lastName}`.toLowerCase().replace(/[^a-z0-9]/g, '') : '');
  // Auto-advance from step 2 after animation completes
  useEffect(() => {
    if (step === 2) {
      const timer = setTimeout(() => setStep(3), 5000); // 5 items * 0.8s + delay
      return () => clearTimeout(timer);
    }
  }, [step]);
  // OTP Resend Countdown
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [resendCountdown]);
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  const handleEmailBlur = () => {
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };
  const handleStep1Submit = () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setStep(1.5);
  };
  // --- Step 1: Entry ---
  const renderStep1 = () => <motion.div key="step1" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="w-full max-w-lg mx-auto" data-id="element-1032">
      <div className="bg-white rounded-3xl p-8 md:p-10 shadow-premium border border-brand-light" data-id="element-1033">
        {slug && <div className="mb-8 text-center" data-id="element-1034">
            <div className="inline-flex items-center gap-2 bg-brand-sage px-4 py-2 rounded-full mb-3" data-id="element-1035">
              <span className="font-mono text-brand-forest font-medium" data-id="element-1036">
                eaves.pro/{slug}
              </span>
              <button className="text-brand-forest hover:text-brand-dark transition-colors" data-id="element-1037">
                <Edit2 size={14} data-id="element-1038" />
              </button>
            </div>
            <p className="text-sm text-brand-muted" data-id="element-1039">
              Great choice — let's finish setting you up.
            </p>
          </div>}

        <div className="text-center mb-8" data-id="element-1040">
          <h1 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1041">
            Let's find your profile
          </h1>
          <p className="text-brand-muted" data-id="element-1042">
            Your Eaves Pro profile may already be built from your public record.
            Tell us your name and where you work.
          </p>
        </div>

        <div className="space-y-5" data-id="element-1043">
          <div className="grid grid-cols-2 gap-4" data-id="element-1044">
            <div data-id="element-1045">
              <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1046">
                First Name
              </label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all" placeholder="Marcus" data-id="element-1047" />
            </div>
            <div data-id="element-1048">
              <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1049">
                Last Name
              </label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all" placeholder="Chen" data-id="element-1050" />
            </div>
          </div>

          <div className="relative" data-id="element-1051">
            <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1052">
              City
            </label>
            <input type="text" value={citySearch} onChange={e => {
            setCitySearch(e.target.value);
            setShowCityDropdown(true);
          }} onFocus={() => setShowCityDropdown(true)} className="w-full bg-brand-offwhite border border-brand-light rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all" placeholder="Start typing a city..." data-id="element-1053" />
            {showCityDropdown && citySearch && <div className="absolute z-10 w-full mt-1 bg-white border border-brand-light rounded-xl shadow-lg max-h-48 overflow-y-auto" data-id="element-1054">
                {CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).map(city => {
              const [cityName, stateAbbr] = city.split(', ');
              return <button key={city} className="w-full text-left px-4 py-2 hover:bg-brand-sage transition-colors flex items-center justify-between" onClick={() => {
                setCitySearch(city);
                setShowCityDropdown(false);
              }} data-id="element-1055">
                      <span className="text-brand-dark" data-id="element-1056">{cityName}</span>
                      <span className="text-brand-muted text-sm" data-id="element-1057">
                        {stateAbbr}
                      </span>
                    </button>;
            })}
              </div>}
          </div>

          <div data-id="element-1058">
            <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1059">
              Email
            </label>
            <input type="email" value={email} onChange={e => {
            setEmail(e.target.value);
            setEmailError('');
          }} onBlur={handleEmailBlur} className={`w-full bg-brand-offwhite border ${emailError ? 'border-red-500' : 'border-brand-light'} rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all`} placeholder="you@example.com" data-id="element-1060" />
            {emailError && <p className="text-red-500 text-sm mt-1.5" data-id="element-1061">{emailError}</p>}
          </div>

          {!slug && displaySlug && <div className="flex flex-col items-center justify-center pt-4 pb-2" data-id="element-1062">
              <p className="text-xs text-brand-muted mb-2" data-id="element-1063">Your profile URL</p>
              <div className="inline-flex items-center gap-2 bg-brand-sage px-4 py-2 rounded-full" data-id="element-1064">
                <span className="font-mono text-brand-forest font-medium" data-id="element-1065">
                  eaves.pro/{displaySlug}
                </span>
                <button className="text-brand-forest hover:text-brand-dark transition-colors" data-id="element-1066">
                  <Edit2 size={14} data-id="element-1067" />
                </button>
              </div>
            </div>}

          <div className="pt-4" data-id="element-1068">
            <button onClick={handleStep1Submit} disabled={!firstName || !lastName || !citySearch || !email} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1069">
              Find my profile
            </button>
            <p className="text-center text-xs text-brand-muted mt-4" data-id="element-1070">
              Free forever. No credit card.
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
  // --- Step 1b: Email Verification (OTP) ---
  const renderStep1b = () => {
    const handleOtpChange = (index: number, value: string) => {
      if (value.length > 1) {
        // Handle paste
        const pastedData = value.slice(0, 6).split('');
        const newOtp = [...otp];
        pastedData.forEach((char, i) => {
          if (index + i < 6) newOtp[index + i] = char;
        });
        setOtp(newOtp);
        // Focus last filled input
        const focusIndex = Math.min(index + pastedData.length, 5);
        otpRefs.current[focusIndex]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
          otpRefs.current[index + 1]?.focus();
        }
      }
      // Auto-submit check
      const currentOtp = [...otp];
      currentOtp[index] = value.slice(0, 1);
      if (currentOtp.every(digit => digit !== '')) {
        setIsVerifying(true);
        setTimeout(() => {
          setStep(2);
          setIsVerifying(false);
        }, 1000);
      }
    };
    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    };
    const handleResend = () => {
      setResendDisabled(true);
      setResendCountdown(30);
      setShowResentMsg(true);
      setTimeout(() => setShowResentMsg(false), 2000);
    };
    return <motion.div key="step1b" initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="w-full max-w-lg mx-auto" data-id="element-1071">
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-premium border border-brand-light text-center" data-id="element-1072">
          <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1073">
            Check your email
          </h2>
          <p className="text-brand-muted mb-8" data-id="element-1074">
            We sent a 6-digit code to{' '}
            <span className="font-medium text-brand-dark" data-id="element-1075">{email}</span>
          </p>

          <div className="flex justify-center gap-2 mb-6" data-id="element-1076">
            {otp.map((digit, index) => <motion.input key={index} initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: index * 0.05
          }} ref={el => otpRefs.current[index] = el} type="text" maxLength={6} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} className="w-12 h-14 text-center text-2xl font-mono bg-brand-offwhite border border-brand-light rounded-xl focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all" data-id="element-1077" />)}
          </div>

          <div className="flex flex-col items-center gap-3 mb-8 text-sm" data-id="element-1078">
            {showResentMsg ? <span className="text-brand-forest flex items-center gap-1" data-id="element-1079">
                Code resent <Check size={14} data-id="element-1080" />
              </span> : resendDisabled ? <span className="text-brand-muted" data-id="element-1081">
                Resend in {resendCountdown}s...
              </span> : <button onClick={handleResend} className="text-brand-forest hover:underline transition-all" data-id="element-1082">
                Didn't get it? Resend code
              </button>}
            <button onClick={() => setStep(1)} className="text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1083">
              Use a different email
            </button>
          </div>

          <button onClick={() => {
          setIsVerifying(true);
          setTimeout(() => {
            setStep(2);
            setIsVerifying(false);
          }, 1000);
        }} disabled={isVerifying || otp.some(d => !d)} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors disabled:opacity-50 flex items-center justify-center h-14" data-id="element-1084">
            {isVerifying ? <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear'
          }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" data-id="element-1085" /> : 'Verify'}
          </button>
          <p className="text-xs text-brand-muted mt-4" data-id="element-1086">
            By continuing, you agree to Eaves'{' '}
            <a href="#" className="underline hover:text-brand-dark" data-id="element-1087">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-brand-dark" data-id="element-1088">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </motion.div>;
  };
  // --- Step 2: Searching (Animated) ---
  const checklist = [{
    id: 1,
    text: 'Verifying your license...',
    doneText: 'License found'
  }, {
    id: 2,
    text: 'Finding your transactions...',
    doneText: 'Transactions found'
  }, {
    id: 3,
    text: 'Pulling your reviews...',
    doneText: 'Reviews found'
  }, {
    id: 4,
    text: 'Searching for social media...',
    doneText: 'Social media found'
  }, {
    id: 5,
    text: 'Assembling your profile...',
    doneText: 'Profile ready'
  }];
  const renderStep2 = () => <motion.div key="step2" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="w-full max-w-lg mx-auto text-center" data-id="element-1089">
      <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-12" data-id="element-1090">
        Searching for your record...
      </h2>
      <div className="space-y-6 max-w-xs mx-auto text-left" data-id="element-1091">
        {checklist.map((item, i) => <ChecklistItem key={item.id} item={item} index={i} data-id="element-1092" />)}
      </div>
    </motion.div>;
  // --- Step 3a: Identity Confirmation ---
  const renderStep3a = () => <motion.div key="step3a" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-lg mx-auto" data-id="element-1093">
      <div className="bg-white rounded-3xl p-8 md:p-10 shadow-premium border border-brand-light text-center" data-id="element-1094">
        <AnimatePresence mode="wait" data-id="element-1095">
          {!isWrongPerson ? <motion.div key="identity" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1096">
              <div className="relative w-24 h-24 mx-auto mb-6" data-id="element-1097">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Agent" className="w-full h-full rounded-full object-cover border-4 border-white shadow-sm" data-id="element-1098" />
                <div className="absolute bottom-0 right-0 w-6 h-6 bg-brand-forest rounded-full border-2 border-white flex items-center justify-center" data-id="element-1099">
                  <Check size={12} className="text-white" data-id="element-1100" />
                </div>
              </div>

              <h2 className="text-2xl font-serif font-semibold text-brand-dark mb-1" data-id="element-1101">
                {firstName || 'Marcus'} {lastName || 'Chen'}
              </h2>
              <p className="text-brand-muted mb-6" data-id="element-1102">The Chen Group · Compass</p>

              <div className="flex flex-col items-center gap-2 mb-8" data-id="element-1103">
                <div className="flex items-center gap-2 text-sm text-brand-muted" data-id="element-1104">
                  <ShieldCheck size={16} className="text-brand-forest" data-id="element-1105" />
                  <span data-id="element-1106">License #01927845 · California</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-brand-muted" data-id="element-1107">
                  <MapPin size={16} data-id="element-1108" />
                  <span data-id="element-1109">{citySearch || 'San Francisco, CA'}</span>
                </div>
              </div>

              <div className="space-y-3" data-id="element-1110">
                <button onClick={() => setConfirmStep('work')} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1111">
                  Yes, that's me
                </button>
                <button onClick={() => setIsWrongPerson(true)} className="w-full bg-white text-brand-muted border border-brand-light rounded-xl py-4 font-medium hover:bg-brand-offwhite hover:text-brand-dark transition-colors" data-id="element-1112">
                  That's not me
                </button>
              </div>
              <div className="mt-4 text-left" data-id="element-1113">
                <button onClick={() => setStep(1)} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1114">
                  ← Back
                </button>
              </div>
            </motion.div> : <motion.div key="wrong-person" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1115">
              <h2 className="text-2xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1116">
                Help us find you
              </h2>
              <p className="text-brand-muted mb-8" data-id="element-1117">
                Enter your real estate license number for a more accurate
                search.
              </p>

              <div className="space-y-5 text-left mb-8" data-id="element-1118">
                <div data-id="element-1119">
                  <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1120">
                    License number
                  </label>
                  <input type="text" value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all font-mono" placeholder="e.g. 01927845" data-id="element-1121" />
                  <p className="text-xs text-brand-muted mt-2" data-id="element-1122">
                    Found on your state license card or your brokerage's website
                  </p>
                </div>
                <div data-id="element-1123">
                  <label className="block text-sm font-medium text-brand-dark mb-1.5" data-id="element-1124">
                    State
                  </label>
                  <select value={licenseState} onChange={e => setLicenseState(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-xl px-4 py-3 focus:outline-none focus:border-brand-forest focus:ring-1 focus:ring-brand-forest/20 transition-all appearance-none" data-id="element-1125">
                    <option value="CA" data-id="element-1126">California</option>
                    <option value="NY" data-id="element-1127">New York</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1128">
                <button onClick={() => {
              setIsWrongPerson(false);
              setStep(2); // Re-run search
            }} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1129">
                  Search again
                </button>
                <Link to="/admin/edit" className="block text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1130">
                  Or build your profile manually
                </Link>
              </div>
              <div className="mt-4 text-left" data-id="element-1131">
                <button onClick={() => setIsWrongPerson(false)} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1132">
                  ← Back
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
  // --- Step 3b: Work Confirmation ---
  const renderStep3b = () => <motion.div key="step3b" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-lg mx-auto" data-id="element-1133">
      <div className="text-center mb-8" data-id="element-1134">
        <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1135">
          Here's what your work looks like
        </h2>
        <p className="text-brand-muted mb-4" data-id="element-1136">
          Verified from public records. Tap any section to make a correction.
        </p>
        <div className="bg-brand-sage/40 rounded-xl px-4 py-3 mb-2 flex items-start gap-2 text-left" data-id="element-1137">
          <Info size={16} className="text-brand-forest shrink-0 mt-0.5" data-id="element-1138" />
          <p className="text-sm text-brand-dark" data-id="element-1139">
            Use the toggles to choose what's visible on your public profile.
            Hidden data won't appear to sellers.
          </p>
        </div>
      </div>

      <div className="space-y-4 mb-8" data-id="element-1140">
        <ToggleMetricCard icon={CheckCircle2} label="Verified Transactions" value="47" detail="Last 24 months · San Francisco, CA" isHidden={hiddenCards.transactions} onToggle={() => setHiddenCards(s => ({
        ...s,
        transactions: !s.transactions
      }))} data-id="element-1141" />
        <ToggleMetricCard icon={TrendingUp} label="Sale Price vs. Market" value="+5.2%" detail="Top 6% in your area" isHidden={hiddenCards.performance} onToggle={() => setHiddenCards(s => ({
        ...s,
        performance: !s.performance
      }))} data-id="element-1142" />
        <EditableMarketsCard icon={MapPin} label="Markets Served" initialZips={['94109', '94115', '94123']} initialDetail="San Francisco metro" data-id="element-1143" />
      </div>

      <div className="space-y-4 text-center" data-id="element-1144">
        <button onClick={() => setConfirmStep('social')} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1145">
          Next
        </button>
        <div className="flex items-center justify-between px-2" data-id="element-1146">
          <button onClick={() => setConfirmStep('identity')} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1147">
            ← Back
          </button>
        </div>
      </div>
    </motion.div>;
  // --- Step 3c: Online Presence ---
  const renderStep3c = () => {
    const availableSocials = [{
      platform: 'Instagram',
      icon: Instagram,
      baseUrl: 'instagram.com/'
    }, {
      platform: 'Facebook',
      icon: Facebook,
      baseUrl: 'facebook.com/'
    }, {
      platform: 'YouTube',
      icon: Youtube,
      baseUrl: 'youtube.com/@'
    }, {
      platform: 'X',
      icon: Twitter,
      baseUrl: 'x.com/'
    }, {
      platform: 'LinkedIn',
      icon: Linkedin,
      baseUrl: 'linkedin.com/in/'
    }, {
      platform: 'TikTok',
      icon: TrendingUp,
      baseUrl: 'tiktok.com/@'
    }, {
      platform: 'Threads',
      icon: MessageSquare,
      baseUrl: 'threads.net/@'
    }];
    return <motion.div key="step3c" initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="w-full max-w-lg mx-auto" data-id="element-1148">
        <div className="text-center mb-8" data-id="element-1149">
          <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1150">
            Your online presence
          </h2>
          <p className="text-brand-muted mb-4" data-id="element-1151">
            We found these accounts linked to your name. Confirm, edit, or
            remove any that aren't yours.
          </p>
          <div className="bg-brand-sage/40 rounded-xl px-4 py-3 mb-2 flex items-start gap-2 text-left" data-id="element-1152">
            <Info size={16} className="text-brand-forest shrink-0 mt-0.5" data-id="element-1153" />
            <p className="text-sm text-brand-dark" data-id="element-1154">
              Tap Edit to modify the URL, X to remove it, or toggle off to hide
              it.
            </p>
          </div>
        </div>

        {/* Group 1: Social Media */}
        <div className="mb-8" data-id="element-1155">
          <h3 className="font-medium text-sm text-brand-dark uppercase tracking-wide mb-3" data-id="element-1156">
            Social Media
          </h3>
          <div className="space-y-4 mb-4" data-id="element-1157">
            {socialAccounts.map(account => <SocialAccountCard key={account.id} icon={account.icon} platform={account.platform} initialHandle={account.handle} initialStatus={account.status} initialIsEditing={account.isEditing} visible={account.visible} onToggleVisible={() => setSocialAccounts(s => s.map(a => a.id === account.id ? {
            ...a,
            visible: !a.visible
          } : a))} onRemove={() => setSocialAccounts(s => s.filter(a => a.id !== account.id))} data-id="element-1158" />)}
          </div>
          <button onClick={() => setShowSocialModal(true)} className="text-brand-forest font-medium hover:text-brand-dark transition-colors flex items-center gap-1" data-id="element-1159">
            <Plus size={16} data-id="element-1160" /> Add a social account
          </button>
        </div>

        {/* Group 2: Websites & Profiles */}
        <div className="mb-8" data-id="element-1161">
          <h3 className="font-medium text-sm text-brand-dark uppercase tracking-wide mb-3 mt-6" data-id="element-1162">
            Websites & Profiles
          </h3>
          <div className="space-y-4 mb-4" data-id="element-1163">
            {websiteAccounts.map(account => <SocialAccountCard key={account.id} icon={account.icon} platform={account.platform} initialHandle={account.handle} initialStatus={account.status} initialIsEditing={account.isEditing} visible={account.visible} onToggleVisible={() => setWebsiteAccounts(s => s.map(a => a.id === account.id ? {
            ...a,
            visible: !a.visible
          } : a))} onRemove={() => setWebsiteAccounts(s => s.filter(a => a.id !== account.id))} data-id="element-1164" />)}
          </div>

          {websiteAccounts.length < 3 && <AnimatePresence data-id="element-1165">
              {showAddWebsite ? <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="bg-white rounded-2xl p-4 border border-brand-light shadow-sm overflow-hidden" data-id="element-1166">
                  <div className="space-y-3 mb-4" data-id="element-1167">
                    <input type="text" placeholder="Title (e.g., My blog)" value={newWebsiteTitle} onChange={e => setNewWebsiteTitle(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-forest" data-id="element-1168" />
                    <input type="text" placeholder="URL (e.g., myblog.com)" value={newWebsiteUrl} onChange={e => setNewWebsiteUrl(e.target.value)} className="w-full bg-brand-offwhite border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-forest" data-id="element-1169" />
                  </div>
                  <div className="flex items-center justify-end gap-2" data-id="element-1170">
                    <button onClick={() => {
                setShowAddWebsite(false);
                setNewWebsiteTitle('');
                setNewWebsiteUrl('');
              }} className="px-3 py-1.5 text-sm font-medium text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1171">
                      Cancel
                    </button>
                    <button onClick={() => {
                if (newWebsiteTitle && newWebsiteUrl) {
                  setWebsiteAccounts([...websiteAccounts, {
                    id: Date.now().toString(),
                    platform: newWebsiteTitle,
                    icon: Globe,
                    handle: newWebsiteUrl,
                    status: 'edited',
                    isEditing: false,
                    visible: true
                  }]);
                  setShowAddWebsite(false);
                  setNewWebsiteTitle('');
                  setNewWebsiteUrl('');
                }
              }} disabled={!newWebsiteTitle || !newWebsiteUrl} className="px-4 py-1.5 text-sm font-medium bg-brand-forest text-white rounded-lg hover:bg-brand-forest/90 transition-colors disabled:opacity-50" data-id="element-1172">
                      Add
                    </button>
                  </div>
                </motion.div> : <button onClick={() => setShowAddWebsite(true)} className="text-brand-forest font-medium hover:text-brand-dark transition-colors flex items-center gap-1" data-id="element-1173">
                  <Plus size={16} data-id="element-1174" /> Add a website or profile
                </button>}
            </AnimatePresence>}
        </div>

        <div className="space-y-4 text-center" data-id="element-1175">
          <button onClick={() => setConfirmStep('reviews')} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1176">
            Next
          </button>
          <div className="flex items-center justify-between px-2" data-id="element-1177">
            <button onClick={() => setConfirmStep('work')} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1178">
              ← Back
            </button>
            <button onClick={() => setConfirmStep('reviews')} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1179">
              Skip — I'll add these later
            </button>
          </div>
        </div>

        {/* Social Modal */}
        <AnimatePresence data-id="element-1180">
          {showSocialModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1181">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="absolute inset-0 bg-black/50" onClick={() => setShowSocialModal(false)} data-id="element-1182" />
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-xl" data-id="element-1183">
                <div className="flex items-center justify-between mb-4" data-id="element-1184">
                  <h3 className="text-lg font-medium text-brand-dark" data-id="element-1185">
                    Add Social Link
                  </h3>
                  <button onClick={() => setShowSocialModal(false)} className="text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1186">
                    <X size={20} data-id="element-1187" />
                  </button>
                </div>
                <div className="space-y-2" data-id="element-1188">
                  {availableSocials.map(social => {
                const isAdded = socialAccounts.some(a => a.platform === social.platform);
                const Icon = social.icon;
                return <button key={social.platform} disabled={isAdded} onClick={() => {
                  setSocialAccounts([...socialAccounts, {
                    id: Date.now().toString(),
                    platform: social.platform,
                    icon: social.icon,
                    handle: social.baseUrl,
                    status: 'edited',
                    isEditing: true,
                    visible: true
                  }]);
                  setShowSocialModal(false);
                }} className={`w-full flex items-center gap-3 p-3 rounded-xl border border-brand-light transition-colors ${isAdded ? 'bg-brand-offwhite text-brand-muted cursor-not-allowed' : 'bg-white hover:border-brand-forest hover:bg-brand-sage/20'}`} data-id="element-1189">
                        <div className="w-8 h-8 rounded-full bg-brand-offwhite flex items-center justify-center shrink-0" data-id="element-1190">
                          <Icon size={16} className={isAdded ? 'text-brand-muted' : 'text-brand-dark'} data-id="element-1191" />
                        </div>
                        <span className="text-sm font-medium" data-id="element-1192">
                          {social.platform}
                        </span>
                        {isAdded && <span className="ml-auto text-xs text-brand-muted" data-id="element-1193">
                            Added
                          </span>}
                      </button>;
              })}
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>
      </motion.div>;
  };
  // --- Step 3d: Reviews Confirmation ---
  const renderStep3d = () => <motion.div key="step3d" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-lg mx-auto" data-id="element-1194">
      <div className="text-center mb-8" data-id="element-1195">
        <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1196">
          What your clients are saying
        </h2>
        <p className="text-brand-muted mb-4" data-id="element-1197">
          Reviews from across the web, all in one place.
        </p>
        <div className="bg-brand-sage/40 rounded-xl px-4 py-3 mb-2 flex items-start gap-2 text-left" data-id="element-1198">
          <Info size={16} className="text-brand-forest shrink-0 mt-0.5" data-id="element-1199" />
          <p className="text-sm text-brand-dark" data-id="element-1200">
            These reviews link to the original source. You control which
            platforms appear on your profile with an optional AI summary of your
            reviews.
          </p>
        </div>
      </div>

      <div className="space-y-4 mb-6" data-id="element-1201">
        {/* Google */}
        <div className={`bg-white rounded-2xl p-5 border border-brand-light shadow-sm flex items-center gap-4 transition-opacity ${!reviewsState.google ? 'opacity-50' : ''}`} data-id="element-1202">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center shrink-0" data-id="element-1203">
            <span className="font-serif font-bold text-brand-forest text-xl" data-id="element-1204">
              G
            </span>
          </div>
          <div className="flex-1" data-id="element-1205">
            <p className="font-medium text-brand-dark" data-id="element-1206">Google Reviews</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1207">
              <Star size={14} className="fill-amber-400 text-amber-400" data-id="element-1208" />
              <span className="text-sm font-medium text-brand-dark" data-id="element-1209">4.9</span>
              <span className="text-sm text-brand-muted" data-id="element-1210">(84 reviews)</span>
            </div>
          </div>
          <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${reviewsState.google ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => setReviewsState(s => ({
          ...s,
          google: !s.google
        }))} data-id="element-1211">
            <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${reviewsState.google ? 'left-6' : 'left-1'}`} data-id="element-1212"></div>
          </button>
        </div>

        {/* Zillow */}
        <div className={`bg-white rounded-2xl p-5 border border-brand-light shadow-sm flex items-center gap-4 transition-opacity ${!reviewsState.zillow ? 'opacity-50' : ''}`} data-id="element-1213">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center shrink-0" data-id="element-1214">
            <span className="font-serif font-bold text-brand-forest text-xl" data-id="element-1215">
              Z
            </span>
          </div>
          <div className="flex-1" data-id="element-1216">
            <p className="font-medium text-brand-dark" data-id="element-1217">Zillow Reviews</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1218">
              <Star size={14} className="fill-amber-400 text-amber-400" data-id="element-1219" />
              <span className="text-sm font-medium text-brand-dark" data-id="element-1220">4.8</span>
              <span className="text-sm text-brand-muted" data-id="element-1221">(128 reviews)</span>
            </div>
          </div>
          <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${reviewsState.zillow ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => setReviewsState(s => ({
          ...s,
          zillow: !s.zillow
        }))} data-id="element-1222">
            <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${reviewsState.zillow ? 'left-6' : 'left-1'}`} data-id="element-1223"></div>
          </button>
        </div>

        {/* Yelp */}
        <div className={`bg-white rounded-2xl p-5 border border-brand-light shadow-sm flex items-center gap-4 transition-opacity ${!reviewsState.yelp ? 'opacity-50' : ''}`} data-id="element-1224">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center shrink-0" data-id="element-1225">
            <span className="font-serif font-bold text-brand-forest text-xl" data-id="element-1226">
              Y
            </span>
          </div>
          <div className="flex-1" data-id="element-1227">
            <p className="font-medium text-brand-dark" data-id="element-1228">Yelp</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1229">
              <Star size={14} className="fill-amber-400 text-amber-400" data-id="element-1230" />
              <span className="text-sm font-medium text-brand-dark" data-id="element-1231">5.0</span>
              <span className="text-sm text-brand-muted" data-id="element-1232">(90 reviews)</span>
            </div>
          </div>
          <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${reviewsState.yelp ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => setReviewsState(s => ({
          ...s,
          yelp: !s.yelp
        }))} data-id="element-1233">
            <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${reviewsState.yelp ? 'left-6' : 'left-1'}`} data-id="element-1234"></div>
          </button>
        </div>

        {/* Facebook */}
        <div className={`bg-white rounded-2xl p-5 border border-brand-light shadow-sm flex items-center gap-4 transition-opacity ${!reviewsState.facebook ? 'opacity-50' : ''}`} data-id="element-1235">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center shrink-0" data-id="element-1236">
            <span className="font-serif font-bold text-brand-forest text-xl" data-id="element-1237">
              F
            </span>
          </div>
          <div className="flex-1" data-id="element-1238">
            <p className="font-medium text-brand-dark" data-id="element-1239">Facebook Reviews</p>
            <div className="flex items-center gap-1 mt-1" data-id="element-1240">
              <Star size={14} className="fill-amber-400 text-amber-400" data-id="element-1241" />
              <span className="text-sm font-medium text-brand-dark" data-id="element-1242">4.7</span>
              <span className="text-sm text-brand-muted" data-id="element-1243">(45 reviews)</span>
            </div>
          </div>
          <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${reviewsState.facebook ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => setReviewsState(s => ({
          ...s,
          facebook: !s.facebook
        }))} data-id="element-1244">
            <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${reviewsState.facebook ? 'left-6' : 'left-1'}`} data-id="element-1245"></div>
          </button>
        </div>
      </div>

      <div className="space-y-4 text-center" data-id="element-1246">
        <button onClick={() => setConfirmStep('contact')} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1247">
          Next
        </button>
        <div className="flex items-center justify-between px-2" data-id="element-1248">
          <button onClick={() => setConfirmStep('social')} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1249">
            ← Back
          </button>
        </div>
      </div>
    </motion.div>;
  // --- Step 3e: Contact Info ---
  const renderStep3e = () => <motion.div key="step3e" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-lg mx-auto" data-id="element-1250">
      <div className="text-center mb-8" data-id="element-1251">
        <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1252">
          Your contact info
        </h2>
        <p className="text-brand-muted" data-id="element-1253">
          This is how clients will reach you from your profile. Verify or update
          your details.
        </p>
      </div>

      <div className="space-y-4 mb-6" data-id="element-1254">
        <ContactInfoCard icon={Mail} label="Email" initialValue={email || 'marcus@chengroup.com'} source="From sign-up" data-id="element-1255" />
        <ContactInfoCard icon={Phone} label="Phone" initialValue="(415) 555-0198" source="Public records" data-id="element-1256" />
        <ContactInfoCard icon={MapPin} label="Office address" initialValue="123 Market St, San Francisco, CA 94109" source="Public records" data-id="element-1257" />
      </div>

      <div className="flex items-center gap-3 mb-8 px-2" data-id="element-1258">
        <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${showContactInfo ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={() => setShowContactInfo(!showContactInfo)} data-id="element-1259">
          <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${showContactInfo ? 'left-6' : 'left-1'}`} data-id="element-1260"></div>
        </button>
        <span className="text-sm font-medium text-brand-dark" data-id="element-1261">
          Show my contact info on my public profile
        </span>
      </div>

      <div className="space-y-4 text-center" data-id="element-1262">
        <button onClick={() => setStep(4)} className="w-full bg-brand-forest text-white rounded-xl py-4 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1263">
          See my profile
        </button>
        <div className="flex items-center justify-between px-2" data-id="element-1264">
          <button onClick={() => setConfirmStep('reviews')} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1265">
            ← Back
          </button>
          <button onClick={() => setStep(4)} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1266">
            I'll update these later
          </button>
        </div>
      </div>
    </motion.div>;
  // --- Step 4: Preview & Publish ---
  const renderStep4 = () => {
    // Confetti particles
    const particles = Array.from({
      length: 200
    }).map((_, i) => {
      const colors = ['#1a4d3e', '#e8f0eb', '#c87b5d', '#f5f0e6', '#4ade80', '#facc15', '#60a5fa'];
      const isRect = Math.random() > 0.5;
      const size = Math.random() * 10 + 6;
      return {
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        angle: Math.random() * 360,
        distance: Math.random() * 1000 + 200,
        width: size,
        height: isRect ? size * (Math.random() * 1.5 + 1.5) : size,
        borderRadius: isRect ? '2px' : '50%',
        delay: Math.random() * 0.8
      };
    });
    return <motion.div key="step4" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="w-full max-w-2xl mx-auto relative" data-id="element-1267">
        {/* Full Page Confetti Burst */}
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center overflow-hidden" data-id="element-1268">
          {particles.map(p => <motion.div key={p.id} initial={{
          x: 0,
          y: 0,
          opacity: 1,
          scale: 0
        }} animate={{
          x: Math.cos(p.angle * Math.PI / 180) * p.distance,
          y: Math.sin(p.angle * Math.PI / 180) * p.distance + 400,
          opacity: 0,
          scale: 1,
          rotate: Math.random() * 720
        }} transition={{
          duration: 3.5,
          delay: p.delay,
          ease: 'easeOut'
        }} className="absolute" style={{
          backgroundColor: p.color,
          width: p.width,
          height: p.height,
          borderRadius: p.borderRadius
        }} data-id="element-1269" />)}
        </div>

        <div className="text-center mb-8 relative z-10" data-id="element-1270">
          <h2 className="text-3xl font-serif font-semibold text-brand-dark mb-2" data-id="element-1271">
            Your profile is ready, {firstName || 'Marcus'}!
          </h2>
          <p className="text-brand-muted" data-id="element-1272">
            Now get it in front of the people who matter. Share your link, or
            make it yours first.
          </p>
        </div>

        {/* Zone 1: Phone Frame Profile Preview */}
        <div className="max-w-[260px] mx-auto mb-6 relative z-10" data-id="element-1273">
          <div className="rounded-[2rem] border-[6px] border-gray-800 bg-white overflow-hidden shadow-2xl relative" data-id="element-1274">
            {/* Notch */}
            <div className="w-24 h-5 bg-gray-800 rounded-b-xl mx-auto absolute top-0 left-1/2 -translate-x-1/2 z-20" data-id="element-1275"></div>

            {/* Scrollable Preview Content */}
            <div className="max-h-[500px] overflow-y-auto bg-brand-offwhite hide-scrollbar pt-6 pb-6" data-id="element-1276">
              <div className="h-24 bg-gradient-to-b from-brand-sage to-brand-sandstoneBg" data-id="element-1277"></div>
              <div className="px-4 pb-4 -mt-12 relative text-center bg-white rounded-t-3xl" data-id="element-1278">
                <div className="w-20 h-20 rounded-full border-4 border-white overflow-hidden bg-brand-light shadow-sm mx-auto mb-3" data-id="element-1279">
                  <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Agent" className="w-full h-full object-cover" data-id="element-1280" />
                </div>
                <h3 className="font-serif font-bold text-lg text-brand-dark leading-tight mb-1" data-id="element-1281">
                  {firstName || 'Marcus'} {lastName || 'Chen'}
                </h3>
                <p className="text-[10px] text-brand-muted mb-3" data-id="element-1282">
                  Principal Agent · The Chen Group
                </p>

                <div className="flex flex-wrap justify-center gap-1.5 mb-4" data-id="element-1283">
                  {reviewsState.google && <div className="bg-brand-offwhite rounded-full px-2 py-1 border border-brand-light flex items-center gap-1" data-id="element-1284">
                      <Star size={10} className="fill-amber-400 text-amber-400" data-id="element-1285" />
                      <span className="text-[10px] font-medium" data-id="element-1286">4.9</span>
                    </div>}
                  {reviewsState.zillow && <div className="bg-brand-offwhite rounded-full px-2 py-1 border border-brand-light flex items-center gap-1" data-id="element-1287">
                      <Star size={10} className="fill-amber-400 text-amber-400" data-id="element-1288" />
                      <span className="text-[10px] font-medium" data-id="element-1289">4.8</span>
                    </div>}
                  {reviewsState.yelp && <div className="bg-brand-offwhite rounded-full px-2 py-1 border border-brand-light flex items-center gap-1" data-id="element-1290">
                      <Star size={10} className="fill-amber-400 text-amber-400" data-id="element-1291" />
                      <span className="text-[10px] font-medium" data-id="element-1292">5.0</span>
                    </div>}
                  {reviewsState.facebook && <div className="bg-brand-offwhite rounded-full px-2 py-1 border border-brand-light flex items-center gap-1" data-id="element-1293">
                      <Star size={10} className="fill-amber-400 text-amber-400" data-id="element-1294" />
                      <span className="text-[10px] font-medium" data-id="element-1295">4.7</span>
                    </div>}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4 text-left" data-id="element-1296">
                  {!hiddenCards.performance && <div className="bg-brand-offwhite rounded-lg p-2.5 border border-brand-light" data-id="element-1297">
                      <div className="flex items-center gap-1 mb-1" data-id="element-1298">
                        <TrendingUp size={10} className="text-brand-forest" data-id="element-1299" />
                        <span className="text-[9px] font-medium text-brand-dark leading-tight" data-id="element-1300">
                          Sale price
                        </span>
                      </div>
                      <span className="font-mono text-xs text-brand-forest font-medium" data-id="element-1301">
                        +5.2%
                      </span>
                    </div>}
                  {!hiddenCards.transactions && <div className="bg-brand-offwhite rounded-lg p-2.5 border border-brand-light" data-id="element-1302">
                      <div className="flex items-center gap-1 mb-1" data-id="element-1303">
                        <CheckCircle2 size={10} className="text-brand-forest" data-id="element-1304" />
                        <span className="text-[9px] font-medium text-brand-dark leading-tight" data-id="element-1305">
                          Transactions
                        </span>
                      </div>
                      <span className="font-mono text-xs text-brand-forest font-medium" data-id="element-1306">
                        347
                      </span>
                    </div>}
                </div>

                <div className="bg-brand-sage/30 rounded-lg p-2.5 border border-brand-sage text-left mb-4" data-id="element-1307">
                  <div className="flex items-center gap-1 mb-1" data-id="element-1308">
                    <MapPin size={10} className="text-brand-forest" data-id="element-1309" />
                    <span className="text-[9px] font-medium text-brand-dark" data-id="element-1310">
                      Markets Served
                    </span>
                  </div>
                  <p className="text-[9px] text-brand-muted font-mono" data-id="element-1311">
                    94109 · 94115 · 94123
                  </p>
                </div>

                {/* Social & Website Links */}
                <div className="space-y-3 text-left" data-id="element-1312">
                  {socialAccounts.filter(a => a.visible).length > 0 && <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-1313">
                      {socialAccounts.filter(a => a.visible).map(account => {
                    const Icon = account.icon;
                    return <div key={account.id} className="w-7 h-7 rounded-full bg-brand-offwhite flex items-center justify-center text-brand-dark border border-brand-light" data-id="element-1314">
                              <Icon size={12} data-id="element-1315" />
                            </div>;
                  })}
                    </div>}

                  {websiteAccounts.filter(a => a.visible).length > 0 && <div className="space-y-1.5" data-id="element-1316">
                      {websiteAccounts.filter(a => a.visible).map(account => <div key={account.id} className="bg-brand-offwhite rounded-lg p-2 border border-brand-light flex items-center gap-2" data-id="element-1317">
                            <account.icon size={12} className="text-brand-muted shrink-0" data-id="element-1318" />
                            <span className="text-[10px] font-medium text-brand-dark truncate" data-id="element-1319">
                              {account.platform}
                            </span>
                          </div>)}
                    </div>}
                </div>

                {/* Contact Info */}
                {showContactInfo && <div className="mt-4 pt-4 border-t border-brand-light space-y-2 text-left" data-id="element-1320">
                    <div className="flex items-center gap-2" data-id="element-1321">
                      <Mail size={10} className="text-brand-muted shrink-0" data-id="element-1322" />
                      <span className="text-[9px] text-brand-dark truncate" data-id="element-1323">
                        {email || 'marcus@chengroup.com'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1324">
                      <Phone size={10} className="text-brand-muted shrink-0" data-id="element-1325" />
                      <span className="text-[9px] text-brand-dark truncate" data-id="element-1326">
                        (415) 555-0198
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1327">
                      <MapPin size={10} className="text-brand-muted shrink-0 mt-0.5" data-id="element-1328" />
                      <span className="text-[9px] text-brand-dark leading-tight" data-id="element-1329">
                        123 Market St, San Francisco, CA 94109
                      </span>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-center" data-id="element-1330">
            <div className="bg-brand-sage rounded-full px-4 py-2 font-mono text-sm text-brand-forest font-medium shadow-sm" data-id="element-1331">
              eaves.pro/{displaySlug || 'marcuschen'}
            </div>
          </div>
        </div>

        {/* Zone 2: Make it yours */}
        <div className="mb-10 relative z-10" data-id="element-1332">
          <h3 className="font-medium text-brand-dark mb-1" data-id="element-1333">Make this yours</h3>
          <p className="text-sm text-brand-muted mb-4" data-id="element-1334">
            The fundamentals are set — your performance, reviews, and
            verification. These personal touches are yours to add.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1335">
            <QuickAddCard icon={Home} title="Add a listing" subtitle="Showcase your active or sold properties" type="listing" onClick={() => navigate('/admin/edit#listing')} data-id="element-1336" />
            <QuickAddCard icon={LinkIcon} title="Add a custom link" subtitle="Your blog, podcast, Linktree, or anything else" type="link" onClick={() => navigate('/admin/edit#link')} data-id="element-1337" />
            <QuickAddCard icon={Video} title="Add a video" subtitle="A listing tour, client testimonial, or intro video" type="video" onClick={() => navigate('/admin/edit#video')} data-id="element-1338" />
          </div>
        </div>

        {/* Zone 3: Actions */}
        <div className="text-center space-y-4 relative z-10" data-id="element-1339">
          <div className="flex gap-3" data-id="element-1340">
            <button onClick={() => {
            setStep(5);
            navigate('/admin/share');
          }} className="flex-1 bg-brand-forest text-white rounded-xl py-3 font-medium hover:bg-brand-forest/90 transition-colors" data-id="element-1341">
              Share my profile
            </button>
            <button onClick={() => {
            setStep(5);
            navigate('/admin/edit');
          }} className="flex-1 bg-white border border-brand-forest text-brand-forest rounded-xl py-3 font-medium hover:bg-brand-sage transition-colors" data-id="element-1342">
              Edit my profile
            </button>
          </div>
          <div className="flex flex-col gap-3" data-id="element-1343">
            <button onClick={() => {
            setStep(3);
            setConfirmStep('contact');
          }} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1344">
              ← Back
            </button>
          </div>
        </div>
      </motion.div>;
  };
  // --- Step 5: Live (Celebration) ---
  const renderStep5 = () => <motion.div key="step5" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="w-full max-w-lg mx-auto pb-24" data-id="element-1345">
      <div className="text-center pt-8 pb-10" data-id="element-1346">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        bounce: 0.5,
        delay: 0.2
      }} className="w-20 h-20 bg-brand-sage rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1347">
          <Check size={40} className="text-brand-forest" strokeWidth={3} data-id="element-1348" />
        </motion.div>
        <h1 className="text-3xl font-serif font-semibold text-brand-dark mb-3" data-id="element-1349">
          Welcome to Eaves Pro, {firstName || 'Marcus'}!
        </h1>
        <p className="text-lg text-brand-muted mb-8" data-id="element-1350">
          You're all set. Your profile is live and ready to share.
        </p>

        <div className="text-center" data-id="element-1351">
          <Link to="/admin" className="inline-block w-full bg-brand-forest text-white rounded-xl py-4 font-medium text-lg hover:bg-brand-forest/90 transition-colors mb-4" data-id="element-1352">
            Go to my dashboard
          </Link>
          <Link to="/marcuschen" className="block text-sm text-brand-forest font-medium hover:text-brand-dark transition-colors" data-id="element-1353">
            View my profile →
          </Link>
        </div>
      </div>
    </motion.div>;
  return <div className="min-h-screen bg-brand-offwhite font-sans selection:bg-brand-sage selection:text-brand-forest flex flex-col" data-id="element-1354">
      {/* Header */}
      <header className="h-16 px-6 flex items-center justify-between shrink-0 border-b border-brand-light bg-white" data-id="element-1355">
        <Link to="/" className="flex items-center gap-2" data-id="element-1356">
          <img src="https://cdn.magicpatterns.com/uploads/tg3VtfRMGFLgjFkX7oevaV/eaves_favicon_transparent.png" alt="Eaves Pro Logo" className="w-8 h-8" data-id="element-1357" />
          <span className="font-serif font-semibold text-xl text-brand-dark hidden sm:inline" data-id="element-1358">
            Eaves Pro
          </span>
        </Link>
        {/* Mobile progress bar - visible on small screens only */}
        <div className="flex items-center gap-3 flex-1 mx-4" data-id="element-1359">
          <div className="flex-1 h-1.5 bg-brand-light rounded-full overflow-hidden" data-id="element-1360">
            <div className="h-full bg-brand-forest rounded-full transition-all duration-500" style={{
            width: `${step === 1 ? 0 : step === 1.5 ? 10 : step === 2 ? 20 : step === 3 ? confirmStep === 'identity' ? 40 : confirmStep === 'work' ? 45 : confirmStep === 'social' ? 50 : confirmStep === 'reviews' ? 55 : confirmStep === 'contact' ? 60 : 40 : step === 4 ? 80 : 100}%`
          }} data-id="element-1361" />
          </div>
          <span className="text-xs font-medium text-brand-forest whitespace-nowrap" data-id="element-1362">
            {step === 1 ? 'Getting started' : step === 1.5 ? 'Verifying email' : step === 2 ? 'Finding your record' : step === 3 ? confirmStep === 'identity' ? 'Review — Identity' : confirmStep === 'work' ? 'Review — Work History' : confirmStep === 'social' ? 'Review — Social' : confirmStep === 'reviews' ? 'Review — Reviews' : confirmStep === 'contact' ? 'Review — Contact' : 'Review' : step === 4 ? 'Profile preview' : 'Share your profile'}
          </span>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden" data-id="element-1363">
        {/* Main Content Area */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-y-auto" data-id="element-1364">
          <AnimatePresence mode="wait" data-id="element-1365">
            {step === 1 && renderStep1()}
            {step === 1.5 && renderStep1b()}
            {step === 2 && renderStep2()}
            {step === 3 && confirmStep === 'identity' && renderStep3a()}
            {step === 3 && confirmStep === 'work' && renderStep3b()}
            {step === 3 && confirmStep === 'social' && renderStep3c()}
            {step === 3 && confirmStep === 'reviews' && renderStep3d()}
            {step === 3 && confirmStep === 'contact' && renderStep3e()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
          </AnimatePresence>
        </main>
      </div>
    </div>;
}
// --- Helper Components ---
function ChecklistItem({
  item,
  index
}: {
  item: {
    id: number;
    text: string;
    doneText: string;
  };
  index: number;
}) {
  const [status, setStatus] = useState<'waiting' | 'active' | 'done'>('waiting');
  useEffect(() => {
    const startDelay = index * 800;
    const completeDelay = startDelay + 800;
    const t1 = setTimeout(() => setStatus('active'), startDelay);
    const t2 = setTimeout(() => setStatus('done'), completeDelay);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [index]);
  return <motion.div initial={{
    opacity: 0,
    x: -10
  }} animate={{
    opacity: status === 'waiting' ? 0 : 1,
    x: status === 'waiting' ? -10 : 0
  }} className="flex items-center gap-3 text-lg" data-id="element-1366">
      <div className="w-6 h-6 flex items-center justify-center shrink-0" data-id="element-1367">
        {status === 'active' && <motion.div animate={{
        scale: [1, 1.2, 1]
      }} transition={{
        repeat: Infinity,
        duration: 1
      }} className="w-2 h-2 bg-brand-forest rounded-full" data-id="element-1368" />}
        {status === 'done' && <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} data-id="element-1369">
            <CheckCircle2 size={24} className="text-brand-forest" data-id="element-1370" />
          </motion.div>}
      </div>
      <span className={`transition-colors duration-300 ${status === 'done' ? 'text-brand-forest font-medium' : 'text-brand-muted'}`} data-id="element-1371">
        {status === 'done' ? item.doneText : item.text}
      </span>
    </motion.div>;
}
function ToggleMetricCard({
  icon: Icon,
  label,
  value,
  detail,
  isHidden,
  onToggle
}: {
  icon: any;
  label: string;
  value: string;
  detail: string;
  isHidden: boolean;
  onToggle: () => void;
}) {
  return <div className="bg-white rounded-2xl border border-brand-light shadow-sm overflow-hidden" data-id="element-1372">
      <div className="p-5 flex items-start justify-between" data-id="element-1373">
        <div className="flex items-center gap-4" data-id="element-1374">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-1375">
            <Icon size={24} data-id="element-1376" />
          </div>
          <div data-id="element-1377">
            <p className="font-medium text-brand-dark" data-id="element-1378">{label}</p>
            {!isHidden && <p className="text-sm text-brand-muted" data-id="element-1379">{detail}</p>}
            {isHidden && <p className="text-sm text-brand-muted italic mt-1" data-id="element-1380">
                Hidden from profile
              </p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2" data-id="element-1381">
          <button className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${!isHidden ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={onToggle} data-id="element-1382">
            <div className={`w-5 h-5 rounded-full bg-white absolute top-1 transition-transform ${!isHidden ? 'left-6' : 'left-1'}`} data-id="element-1383"></div>
          </button>
        </div>
      </div>

      {!isHidden && <div className="px-5 pb-5 flex items-end justify-between" data-id="element-1384">
          <div data-id="element-1385"></div>
          <div className="text-right" data-id="element-1386">
            <div className="font-mono text-2xl text-brand-forest font-medium mb-1" data-id="element-1387">
              {value}
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-brand-forest bg-brand-sage px-2 py-0.5 rounded-full" data-id="element-1388">
              <Check size={10} data-id="element-1389" /> Public records
            </span>
          </div>
        </div>}
    </div>;
}
function EditableMarketsCard({
  icon: Icon,
  label,
  initialZips,
  initialDetail
}: {
  icon: any;
  label: string;
  initialZips: string[];
  initialDetail: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [zips, setZips] = useState(initialZips);
  const [detail, setDetail] = useState(initialDetail);
  const [isEdited, setIsEdited] = useState(false);
  const [tempZips, setTempZips] = useState(zips);
  const [tempDetail, setTempDetail] = useState(detail);
  if (isRemoved) return null;
  return <div className="bg-white rounded-2xl border border-brand-light shadow-sm overflow-hidden" data-id="element-1390">
      <div className="p-5 flex items-center justify-between" data-id="element-1391">
        <div className="flex items-center gap-4" data-id="element-1392">
          <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-1393">
            <Icon size={24} data-id="element-1394" />
          </div>
          <div data-id="element-1395">
            <p className="font-medium text-brand-dark" data-id="element-1396">{label}</p>
            <p className="text-sm text-brand-muted" data-id="element-1397">{detail}</p>
          </div>
        </div>
        <div className="text-right" data-id="element-1398">
          <div className="flex gap-1.5 mb-2 justify-end" data-id="element-1399">
            {zips.map((zip, i) => <span key={i} className="bg-brand-sage text-brand-forest px-2 py-1 rounded-md text-xs font-mono" data-id="element-1400">
                {zip}
              </span>)}
          </div>
          {isEdited ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-brand-copper bg-brand-copperLight px-2 py-0.5 rounded-full" data-id="element-1401">
              <Edit2 size={10} data-id="element-1402" /> Edited
            </span> : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-brand-forest bg-brand-sage px-2 py-0.5 rounded-full" data-id="element-1403">
              <Check size={10} data-id="element-1404" /> Public records
            </span>}
        </div>
      </div>

      <AnimatePresence data-id="element-1405">
        {expanded ? <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="border-t border-brand-light bg-brand-offwhite p-4" data-id="element-1406">
            <div className="space-y-3 mb-4" data-id="element-1407">
              <div data-id="element-1408">
                <label className="block text-xs font-medium text-brand-dark mb-1" data-id="element-1409">
                  ZIP Codes
                </label>
                <div className="space-y-2" data-id="element-1410">
                  {tempZips.map((zip, i) => <div key={i} className="flex gap-2" data-id="element-1411">
                      <input type="text" value={zip} onChange={e => {
                  const newZips = [...tempZips];
                  newZips[i] = e.target.value;
                  setTempZips(newZips);
                }} className="flex-1 bg-white border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-forest font-mono" data-id="element-1412" />
                      <button onClick={() => {
                  setTempZips(tempZips.filter((_, idx) => idx !== i));
                }} className="p-2 text-brand-muted hover:text-red-500 transition-colors" data-id="element-1413">
                        <X size={16} data-id="element-1414" />
                      </button>
                    </div>)}
                  <button onClick={() => setTempZips([...tempZips, ''])} className="text-xs font-medium text-brand-forest hover:text-brand-dark transition-colors flex items-center gap-1" data-id="element-1415">
                    <Plus size={12} data-id="element-1416" /> Add ZIP
                  </button>
                </div>
              </div>
              <div data-id="element-1417">
                <label className="block text-xs font-medium text-brand-dark mb-1" data-id="element-1418">
                  Detail
                </label>
                <input type="text" value={tempDetail} onChange={e => setTempDetail(e.target.value)} className="w-full bg-white border border-brand-light rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-forest" data-id="element-1419" />
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-1420">
              <button onClick={() => setIsRemoved(true)} className="text-sm text-brand-muted hover:text-red-500 transition-colors" data-id="element-1421">
                Remove this section
              </button>
              <div className="flex gap-2" data-id="element-1422">
                <button onClick={() => setExpanded(false)} className="px-3 py-1.5 text-sm font-medium text-brand-muted hover:text-brand-dark transition-colors" data-id="element-1423">
                  Cancel
                </button>
                <button onClick={() => {
              setZips(tempZips.filter(z => z.trim() !== ''));
              setDetail(tempDetail);
              setIsEdited(true);
              setExpanded(false);
            }} className="px-4 py-1.5 text-sm font-medium bg-brand-forest text-white rounded-lg hover:bg-brand-forest/90 transition-colors" data-id="element-1424">
                  Save changes
                </button>
              </div>
            </div>
          </motion.div> : <div className="border-t border-brand-light bg-brand-offwhite/50 p-2 text-center" data-id="element-1425">
            <button onClick={() => {
          setTempZips(zips);
          setTempDetail(detail);
          setExpanded(true);
        }} className="text-xs font-medium text-brand-forest hover:text-brand-dark transition-colors" data-id="element-1426">
              Update information
            </button>
          </div>}
      </AnimatePresence>
    </div>;
}
function SocialAccountCard({
  icon: Icon,
  platform,
  initialHandle,
  initialStatus = 'unconfirmed',
  initialIsEditing = false,
  visible = true,
  onToggleVisible,
  onRemove
}: {
  icon: any;
  platform: string;
  initialHandle: string;
  initialStatus?: 'unconfirmed' | 'confirmed' | 'edited';
  initialIsEditing?: boolean;
  visible?: boolean;
  onToggleVisible?: () => void;
  onRemove?: () => void;
}) {
  const [status, setStatus] = useState<'unconfirmed' | 'confirmed' | 'edited'>(initialStatus);
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const [handle, setHandle] = useState(initialHandle);
  const [tempHandle, setTempHandle] = useState(handle);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);
  return <div className={`bg-white rounded-2xl p-4 border shadow-sm flex items-center gap-4 transition-all ${status === 'confirmed' ? 'border-brand-sage border-l-4' : 'border-brand-light'} ${!visible ? 'opacity-50' : ''}`} data-id="element-1427">
      <div className="w-10 h-10 rounded-full bg-brand-offwhite flex items-center justify-center shrink-0" data-id="element-1428">
        <Icon size={18} className="text-brand-muted" data-id="element-1429" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-1430">
        <p className="font-medium text-brand-dark text-sm" data-id="element-1431">{platform}</p>
        {isEditing ? <input ref={inputRef} type="text" value={tempHandle} onChange={e => setTempHandle(e.target.value)} className="w-full mt-1 bg-brand-offwhite border border-brand-light rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-brand-forest" data-id="element-1432" /> : <p className="text-xs text-brand-muted font-mono truncate" data-id="element-1433">
            {handle}
          </p>}
      </div>

      <div className="flex items-center gap-2 shrink-0" data-id="element-1434">
        {isEditing ? <>
            <button onClick={() => {
          setIsEditing(false);
          if (status === 'unconfirmed' && !handle) {
            onRemove?.();
          }
        }} className="p-1.5 text-brand-muted hover:bg-brand-offwhite rounded-lg transition-colors" data-id="element-1435">
              <X size={14} data-id="element-1436" />
            </button>
            <button onClick={() => {
          setHandle(tempHandle);
          setStatus('edited');
          setIsEditing(false);
        }} className="p-1.5 text-brand-forest hover:bg-brand-sage rounded-lg transition-colors" data-id="element-1437">
              <Check size={14} data-id="element-1438" />
            </button>
          </> : <>
            {status === 'confirmed' && <span className="text-[10px] font-medium text-brand-forest bg-brand-sage px-2 py-0.5 rounded-full mr-2" data-id="element-1439">
                ✓ Confirmed
              </span>}
            {status === 'edited' && <span className="text-[10px] font-medium text-brand-copper bg-brand-copperLight px-2 py-0.5 rounded-full mr-2" data-id="element-1440">
                ✎ Edited
              </span>}

            {onToggleVisible && <button className={`w-10 h-6 rounded-full transition-colors relative shrink-0 mr-1 ${visible ? 'bg-brand-forest' : 'bg-gray-200'}`} onClick={onToggleVisible} data-id="element-1441">
                <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${visible ? 'left-5' : 'left-1'}`} data-id="element-1442"></div>
              </button>}

            <button onClick={() => {
          setTempHandle(handle);
          setIsEditing(true);
        }} className="p-1.5 text-brand-muted hover:text-brand-dark hover:bg-brand-offwhite rounded-lg transition-colors" title="Edit" data-id="element-1443">
              <Edit2 size={14} data-id="element-1444" />
            </button>
            <button onClick={() => onRemove?.()} className="p-1.5 text-brand-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove" data-id="element-1445">
              <X size={16} data-id="element-1446" />
            </button>
          </>}
      </div>
    </div>;
}
function ContactInfoCard({
  icon: Icon,
  label,
  initialValue,
  source
}: {
  icon: any;
  label: string;
  initialValue: string;
  source: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [tempValue, setTempValue] = useState(value);
  const [isEdited, setIsEdited] = useState(false);
  return <div className="bg-white rounded-2xl p-5 border border-brand-light shadow-sm flex items-center gap-4" data-id="element-1447">
      <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center shrink-0" data-id="element-1448">
        <Icon size={18} className="text-brand-forest" data-id="element-1449" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-1450">
        <div className="flex items-center gap-2 mb-1" data-id="element-1451">
          <p className="font-medium text-brand-dark text-sm" data-id="element-1452">{label}</p>
          {isEdited ? <span className="text-[9px] font-medium text-brand-copper bg-brand-copperLight px-1.5 py-0.5 rounded-full" data-id="element-1453">
              ✎ Edited
            </span> : <span className="text-[9px] font-medium text-brand-forest bg-brand-sage px-1.5 py-0.5 rounded-full" data-id="element-1454">
              ✓ {source}
            </span>}
        </div>
        {isEditing ? <div className="flex gap-2 mt-2" data-id="element-1455">
            <input type="text" value={tempValue} onChange={e => setTempValue(e.target.value)} className="flex-1 bg-brand-offwhite border border-brand-light rounded px-2 py-1 text-sm focus:outline-none focus:border-brand-forest" autoFocus data-id="element-1456" />
            <button onClick={() => setIsEditing(false)} className="px-2 py-1 text-xs font-medium text-brand-muted hover:bg-brand-offwhite rounded transition-colors" data-id="element-1457">
              Cancel
            </button>
            <button onClick={() => {
          setValue(tempValue);
          if (tempValue !== initialValue) setIsEdited(true);
          setIsEditing(false);
        }} className="px-2 py-1 text-xs font-medium bg-brand-forest text-white rounded hover:bg-brand-forest/90 transition-colors" data-id="element-1458">
              Save
            </button>
          </div> : <p className="font-medium text-brand-dark truncate" data-id="element-1459">{value}</p>}
      </div>
      {!isEditing && <button onClick={() => {
      setTempValue(value);
      setIsEditing(true);
    }} className="p-2 text-brand-muted hover:text-brand-dark hover:bg-brand-offwhite rounded-lg transition-colors shrink-0" data-id="element-1460">
          <Edit2 size={16} data-id="element-1461" />
        </button>}
    </div>;
}
function QuickAddCard({
  icon: Icon,
  title,
  subtitle,
  type,
  onClick
}: {
  icon: any;
  title: string;
  subtitle: string;
  type: 'listing' | 'link' | 'video';
  onClick?: () => void;
}) {
  return <div className="w-full bg-white rounded-2xl border border-brand-light border-dashed cursor-pointer hover:border-brand-forest hover:bg-brand-offwhite/50 shadow-sm p-4 transition-colors" onClick={onClick} data-id="element-1462">
      <div className="flex flex-col items-center text-center" data-id="element-1463">
        <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center mb-3" data-id="element-1464">
          <Icon size={18} className="text-brand-forest" data-id="element-1465" />
        </div>
        <h4 className="font-medium text-brand-dark text-sm mb-1" data-id="element-1466">{title}</h4>
        <p className="text-xs text-brand-muted mb-2" data-id="element-1467">{subtitle}</p>
      </div>
    </div>;
}